import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, Input, DatePicker, Button, Space, Card, Row, Col, Switch, InputNumber, Upload, message, Select } from 'antd';
import { DragOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LocationLookup from '../../../components/LocationLookup';
import dayjs from '../../../dayjs';
import config from '../../../config';
import { ReactComponent as Trash } from '../../../assets/icons/Trash.svg';
import { ReactComponent as UploadIcon } from '../../../assets/icons/Upload.svg';
import { ReactComponent as Oneway } from '../../../assets/icons/Oneway.svg';
import { ReactComponent as Return } from '../../../assets/icons/Return.svg';
import useAllStops from '../../../hooks/useAllStops';
import TimeField from 'react-simple-timefield';
import { Time } from '../../../components/Icons';
import { last } from 'lodash';
import { getCachedPathDebounced } from '../../../libs/pathLib';
import { getDepartureTimeAsDayjs } from '../../../libs/routes-lib';
import { getSecondsSinceMidnightAsDayjs, getSecondsSinceMidnightFromDayjs } from '../../../libs/formatLib';

const props = {
    name: 'file',
    action: 'https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload',
    headers: {
        authorization: 'authorization-text',
    },
    onChange(info) {
        if (info.file.status !== 'uploading') {
            console.log(info.file, info.fileList);
        }
        if (info.file.status === 'done') {
            message.success(`${info.file.name} file uploaded successfully`);
        } else if (info.file.status === 'error') {
            message.error(`${info.file.name} file upload failed.`);
        }
    },
};

export const StopItem = ({ stop, index, onDelete, onChange, isFirst, isLast, previousStop, onReturnToggle, returnEnabled, allStops, updateSubsequentStops, disabledEdit = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: stop.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const [arrivalTimeState, setArrivalTimeState] = React.useState(null);

    const calculateArrivalTime = async (fromStop, toStop) => {
        if(!fromStop?.lat || !fromStop?.lon || !toStop?.lat || !toStop?.lon) {
            return null;
        }
        const {duration, ...rest} = await getCachedPathDebounced({profile: 'bus', startCoord: fromStop, endCoord: toStop});
        const departTime = fromStop.departTime;
        if (departTime) {
            const arrivalTime = departTime.add(duration, 'second');
            const secondsSinceMidnight = getSecondsSinceMidnightFromDayjs(arrivalTime);
            return {arrivalTime, duration, secondsSinceMidnight: secondsSinceMidnight};
        }
        return null;
    };

    // const calculateArrivalTime = async (fromStop, toStop) => {
    //     if(!fromStop?.lat || !fromStop?.lon || !toStop?.lat || !toStop?.lon) {
    //         return dayjs();
    //     }

    //     try {
    //         // Format coordinates correctly for the API
    //         const response = await fetch(
    //             `https://api.geoapify.com/v1/routing?waypoints=${fromCoordinates[1]},${fromCoordinates[0]}|${toCoordinates[1]},${toCoordinates[0]}&mode=drive&apiKey=${config.maps.geoapify.ctrxs}`
    //         );

    //         if (!response.ok) {
    //             console.error('API response not OK:', response.status, response.statusText);
    //             return null;
    //         }

    //         const data = await response.json();

    //         if (data.features && data.features[0]) {
    //             // Extract duration from the correct property in the response
    //             const duration = data.features[0].properties.time;
    //             const departTime = fromStop.departTime;
    //             if (departTime) {
    //                 const arrivalTime = departTime.add(duration, 'second');
    //                 return arrivalTime;
    //             }
    //         }
    //     } catch (error) {
    //         console.error('Error calculating arrival time:', error);
    //     }
    //     return null;
    // };

    const handleTimeChange = async (e, time) => {
        const value = dayjs(time, 'HH:mm');
        const changes = {
            ...(isFirst ? { arriveTime: value } : {}),
            departTime: value
        }
        onChange(index, changes);

        // Recalculate arrival time for this stop and all subsequent stops
        if (time && previousStop?.lat && previousStop?.lon && stop?.lat && stop?.lon) {
            const {arrivalTime, secondsSinceMidnight} = await calculateArrivalTime(previousStop, stop);
            if (arrivalTime) {
                onChange(index, {
                    ...changes,
                    estimatedArrivalTime: arrivalTime,
                    departSecs: secondsSinceMidnight,
                    arriveSecs: secondsSinceMidnight,
                    arriveTime: arrivalTime,
                    departTime: arrivalTime,
                });
                setArrivalTimeState(arrivalTime);
            }
        }
    };

    const handleDateChange = (date) => {
        // Store the old date for comparison
        const oldDate = isFirst ? stop.arriveDate : stop.departDate;

        // Update the current stop's date
        onChange(index, {
            departDate: date,
            arriveDate: date
        });

        // If we have a valid new date
        if (date) {
            if (oldDate) {
                // If we have an old date, calculate the difference
                const diffDays = date.diff(oldDate, 'day');

                // If there's a difference, update all subsequent stops
                if (diffDays !== 0) {
                    updateSubsequentStops(index, diffDays, date);
                }
            } else {
                // If this is the first time setting a date, update all subsequent stops with this date
                updateSubsequentStops(index, 0, date);
            }
        }
    };

    // Calculate arrival time when component mounts or when dependencies change
    useEffect(() => {
        if (isLast && previousStop) {
            calculateArrivalTime(previousStop, stop).then(props => {
                if (props) {
                    const {arrivalTime} = props;
                    setArrivalTimeState(arrivalTime);
                }
            });
        }
    }, [isLast, previousStop, stop.coordinates, stop.departTime]);

    return (
        <div ref={setNodeRef} style={style} className={`stop-item ${isDragging ? 'dragging' : ''}`}>
            <Card
                size="small"
                className="stop-card"
                title={
                    <div className="stop-header">
                        <div className={`d-flex align-items-center ${disabledEdit ? 'text-muted pl-2' : ''}`}>
                            {!disabledEdit && <div {...attributes} {...listeners} className="drag-handle">
                                <DragOutlined />
                            </div>}
                            <span>
                                {isFirst ? 'Initial Pickup' :
                                    isLast && !returnEnabled && !disabledEdit ? 'Final Drop-off' :
                                        isLast && !returnEnabled && disabledEdit ? 'Final Drop-off' :
                                            `Stop ${index}`}
                            </span>
                        </div>
                        {/* {isFirst ?
                            <div className='d-flex align-items-center' style={{ gap: 6 }}>
                                <span className='text-muted font-weight-normal'>Return</span>
                                <Switch size="small" checked={stop.returnEnabled} onChange={(checked) => onReturnToggle(checked)} />
                            </div>
                            : null} */}
                    </div>
                }
                extra={
                    (!isLast && !isFirst) || (isLast && returnEnabled) ? (
                        <Button className='py-0'
                            type="text"
                            icon={<Trash style={{ height: 14 }} />}
                            onClick={() => onDelete(index)}
                            style={{ height: 24, width: 24 }}
                        />
                    ) : null
                }
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    {disabledEdit ?
                        <div className='text-muted font-weight-normal'>
                            {stop.stopName}
                        </div>
                        :
                        <Form.Item label={isFirst ? 'Pickup Location' : isLast ? 'Drop-off Location' : 'Stop Location'}>
                            <LocationLookup
                                defaultValue={stop?.location || allStops[stop?.stopId]?.stopName}
                                allStops={allStops}
                                location={{value: stop.location}}
                                setLocation={async (value, option) => {
                                    if (!value || !option) {
                                        console.warn('Invalid location data received');
                                        return;
                                    }
                                    const { lon, lat } = option;
                                    const cords = [lon, lat];
                                    const location = {
                                        location: value,
                                        center: cords,
                                        coordinates: cords,
                                        stopType: 'charter',
                                        ...option
                                    }
                                    onChange(index, location);

                                    // Calculate arrival time if we have a previous stop
                                    if (previousStop && previousStop.lat && previousStop.lon) {
                                        const {arrivalTime, secondsSinceMidnight} = await calculateArrivalTime(previousStop, location);
                                        if (arrivalTime) {
                                            onChange(index, {...location, 
                                                estimatedArrivalTime: arrivalTime, 
                                                arriveTime: arrivalTime,
                                                departTime: arrivalTime,
                                                departSecs: secondsSinceMidnight,
                                                arriveSecs: secondsSinceMidnight
                                            });
                                            setArrivalTimeState(arrivalTime);
                                        }
                                    }
                                }}
                                placeholder="Enter location"
                                style={{ width: '100%' }}
                                type="google"
                            />
                        </Form.Item>
                    }
                    {!(isLast && !returnEnabled) && (
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item label={isFirst ? 'Pickup Date' : 'Depart Date'}>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={isFirst ? stop.arriveDate : stop.departDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        placeholder={isFirst ? 'Select pickup date' : 'Select departure date'}
                                        minDate={isFirst ? null : previousStop?.departDate}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label={isFirst ? 'Pickup Time' : 'Depart Time'}>
                                    <div className="time-field-container">
                                        <TimeField
                                            className={`ant-input ant-input-sm inputtext-small text-center`}
                                            style={{width: '100%', paddingRight: '30px'}}
                                            value={(isFirst ? stop.arriveTime : stop.departTime)?.format?.('HH:mm')}
                                            format="HH:mm"
                                            onChange={handleTimeChange}
                                        />
                                        <Time className="time-field-icon" />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}
                    {arrivalTimeState && (
                        <div className='text-muted font-weight-normal'>
                            Est. arrival: {arrivalTimeState.format('DD/MM/YYYY h:mm A')}
                        </div>
                    )}
                </Space>
            </Card>
        </div>
    );
};

const ItineraryStep = ({ data, onChange, isEmbedded = false }) => {
    const { allDepots } = useAllStops();
    const [form] = Form.useForm();
    const { stops = [], passengers } = data;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = stops.findIndex((stop) => stop.id === active.id);
            const newIndex = stops.findIndex((stop) => stop.id === over.id);
            const oldStop = stops[oldIndex];
            const newStop = stops[newIndex];

            if (oldStop.returnEnabled) {
                newStop.returnEnabled = true;
                oldStop.returnEnabled = false;
            } else if (newStop.returnEnabled) {
                newStop.returnEnabled = false;
                oldStop.returnEnabled = true;
            }

            const newStops = [...stops];
            newStops[oldIndex] = newStop;
            newStops[newIndex] = oldStop;
            onChange({ ...data, stops: newStops });
        }
    };

    const handleAddStop = (beforeLast = true) => {
        const newStop = {
            id: `stop-${Date.now()}`,
            type: 'stop',
            location: '',
            coordinates: null,
            name: '',
            address: '',
            departDate: null,
            departTime: null,
            estimatedArrivalTime: null
        };

        // Insert the new stop just before the last stop (destination)
        const newStops = [...stops];

        const updatedStop = (newStop, lastStop) => {
            newStop.departDate = lastStop.departDate;
            newStop.departTime = lastStop.departTime;
            newStop.arriveDate = lastStop.arriveDate;
            newStop.arriveTime = lastStop.arriveTime;
            newStop.arriveSecs = lastStop.arriveSecs;
            newStop.departSecs = lastStop.departSecs;
        }
        if (beforeLast) {
            updatedStop(newStop, newStops[newStops.length - 2]);
            newStops.splice(-1, 0, newStop);
        } else {
            updatedStop(newStop, last(newStops));
            newStops.push(newStop);
        }

        onChange({ ...data, stops: newStops });
    };

    const handleDeleteStop = (index) => {
        const newStops = stops.filter((_, i) => i !== index);
        onChange({ ...data, stops: newStops });
    };

    const handleStopChange = (index, changes) => {
        const newStops = [...stops];
        newStops[index] = { ...newStops[index], ...changes };
        onChange({ ...data, stops: newStops });
    };

    const updateSubsequentStops = (index, diffDays, newDate) => {
        const newStops = [...stops];
        const changedStop = newStops[index];

        // Update all stops after the current one
        for (let i = index; i < newStops.length; i++) {
            if (newStops[i].departDate) {
                // Add the difference in days to each subsequent stop's date
                newStops[i] = {
                    ...newStops[i],
                    departDate: newStops[i].departDate.add(diffDays, 'day'),
                    arriveDate: newStops[i].arriveDate.add(diffDays, 'day')
                };
            } else if (newDate) {
                // If the stop doesn't have a date but we have a new date, use that date
                newStops[i] = {
                    ...newStops[i],
                    departDate: newDate.clone(),
                    arriveDate: newDate.clone()
                };
            }

            // Also set the time if it's not set
            if (!newStops[i].departTime && changedStop.departTime) {
                newStops[i] = {
                    ...newStops[i],
                    departTime: changedStop.departTime.clone(),
                    arriveTime: changedStop.arriveTime.clone()
                };
            }
        }

        onChange({ ...data, stops: newStops });
    };

    const handlePassengerChange = (value) => {
        onChange({ ...data, passengers: value || 0 });
    };

    const handleStartDepotChange = async (depotId) => {
        const depot = allDepots[depotId];
        let startDepotDistance = 0;
        let startDepotDuration = 0;
        if(depot?.lat && depot?.lon && data.stops?.[0]?.lat && data.stops?.[0]?.lon) {
          const {distance, duration} = await getCachedPathDebounced({profile: 'bus', startCoord: depot, endCoord: data.stops[0]}); 
          startDepotDistance = distance;
          startDepotDuration = duration;
        }
        onChange({
            ...data,
            startDepot: depot,
            startDepotDistance: startDepotDistance,
            startDepotDuration: startDepotDuration
        });
    };

    const handleEndDepotChange = async (depotId) => {
        const depot = allDepots[depotId];
        let endDepotDistance = 0;
        let endDepotDuration = 0;
        if(depot?.lat && depot?.lon && last(data.stops)?.lat && last(data.stops)?.lon) {
          const {distance, duration} = await getCachedPathDebounced({profile: 'bus', startCoord: last(data.stops), endCoord: depot}); 
          endDepotDistance = distance;
          endDepotDuration = duration;
        }   
        onChange({
            ...data,
            endDepot: depot,
            endDepotDistance: endDepotDistance,
            endDepotDuration: endDepotDuration
        });
    };

    const handleReturnToggle = (checked) => {
        const newStops = [...stops];
        newStops[0] = { ...newStops[0], returnEnabled: checked };
        onChange({ ...data, stops: newStops });
    };

    const handleReturnChange = (enabled) => {
        const newStops = [...stops];
        newStops[0] = { ...newStops[0], returnEnabled: enabled };
        onChange({ ...data, stops: newStops });
    };

    // Check if return is enabled
    const returnEnabled = stops.length > 0 && stops[0].returnEnabled;
    const firstStop = stops[0];

    return (
        <div className="itinerary-step">
            <Form form={form} layout="vertical">
                {!isEmbedded && <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Start Depot">
                            <Select
                                placeholder="Select start depot"
                                value={data.startDepot?.stopId}
                                onChange={handleStartDepotChange}
                                size='large'
                                options={Object.values(allDepots || {}).map(depot => ({
                                    value: depot.stopId,
                                    label: depot.stopName
                                }))}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                </Row>}
                <div className='charter-type-button-wrap'>
                    <div className={`charter-type-button ${!returnEnabled ? 'active' : ''}`} onClick={() => handleReturnChange(false)}>
                        <Oneway style={{ height: 34 }} />
                        <strong>One Way</strong>
                    </div>
                    <div className={`charter-type-button ${returnEnabled ? 'active' : ''}`} onClick={() => handleReturnChange(true)}>
                        <Return style={{ height: 30 }} />
                        <strong>Return Trip</strong>
                    </div>
                </div>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div className="stops-container">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={stops.map(stop => stop.id)} strategy={verticalListSortingStrategy}>
                                {stops.map((stop, index) => {
                                    // Render the "Add Stop" button before the destination (last stop)
                                    if (!returnEnabled && index === stops.length - 1) {
                                        return (
                                            <React.Fragment key={stop.id}>
                                                <div className='button-separator'>
                                                    <Button type="primary" className="icon-button" icon={<PlusOutlined />} onClick={handleAddStop}>Add Stop</Button>
                                                </div>
                                                <StopItem
                                                    stop={stop}
                                                    index={index}
                                                    onDelete={handleDeleteStop}
                                                    onChange={handleStopChange}
                                                    isFirst={index === 0}
                                                    isLast={index === stops.length - 1}
                                                    previousStop={index > 0 ? stops[index - 1] : null}
                                                    onReturnToggle={handleReturnToggle}
                                                    returnEnabled={returnEnabled}
                                                    allStops={stops}
                                                    updateSubsequentStops={updateSubsequentStops}
                                                />
                                            </React.Fragment>
                                        );
                                    }

                                    return (
                                        <StopItem
                                            key={stop.id}
                                            stop={stop}
                                            index={index}
                                            onDelete={handleDeleteStop}
                                            onChange={handleStopChange}
                                            isFirst={index === 0}
                                            isLast={index === stops.length - 1}
                                            previousStop={index > 0 ? stops[index - 1] : null}
                                            onReturnToggle={handleReturnToggle}
                                            returnEnabled={returnEnabled}
                                            allStops={stops}
                                            updateSubsequentStops={updateSubsequentStops}
                                        />
                                    );
                                })}
                                {returnEnabled && stops.length > 0 && (
                                    <div className='button-separator'>
                                        <Button type="primary" className="icon-button" icon={<PlusOutlined />} onClick={() => handleAddStop(false)}>Add Stop</Button>
                                    </div>
                                )}
                            </SortableContext>
                        </DndContext>

                        {/* Return Destination Card - Only shown when return is enabled */}
                        {returnEnabled && stops.length > 0 && (
                            <StopItem
                                key={firstStop.id}
                                stop={firstStop}
                                index={stops.length - 1}
                                onDelete={handleDeleteStop}
                                onChange={handleStopChange}
                                isFirst={false}
                                isLast={true}
                                previousStop={stops[stops.length - 1]}
                                onReturnToggle={handleReturnToggle}
                                returnEnabled={false}
                                allStops={stops}
                                updateSubsequentStops={updateSubsequentStops}
                                disabledEdit={true}
                            />
                        )}

                        {!isEmbedded && <Row gutter={16} className='mt-4'>
                            <Col span={24}>
                                <Form.Item label="End Depot">
                                    <Select
                                        placeholder="Select end depot"
                                        value={data.endDepot?.stopId}
                                        onChange={handleEndDepotChange}
                                        size='large'
                                        options={Object.values(allDepots || {}).map(depot => ({
                                            value: depot.stopId,
                                            label: depot.stopName
                                        }))}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                        </Row>}

                        <InputNumber
                            placeholder="No. of passengers"
                            value={passengers}
                            onChange={handlePassengerChange}
                            min={0}
                            className='w-100 mt-2'
                            size="large"
                        />

                        {/* <Upload {...props} className='upload-button'>
                            <Button icon={<UploadIcon />}>Upload Itinerary</Button>
                        </Upload> */}
                    </div>
                </Space>
            </Form>
        </div>
    );
};

export default ItineraryStep; 